import { useEffect, useState } from "react";
import { Form } from "antd";

import Spinner from "../../../../components/layout/spinner/Spinner";
import TableCustom from "../../../../components/ui/TableCustom";
import DrawerCustom from "../../../../components/custom/DrawerCustom";
import getInitialMarket from "../../../../utils/getInitialMarket";

import { useGetFeaturedEventsQuery } from "../../../../graphql/operations/trialMode/localEvent/get-featured-events";
import { useCreateFeaturedEventMutation } from "../../../../graphql/operations/trialMode/localEvent/create-featured-event";
import { useUploadFileMutation } from "../../../../graphql/operations/upload-file";
import { useUpdateFeaturedEventMutation } from "../../../../graphql/operations/trialMode/localEvent/update-featured-event";
import { useUpdateFeaturedEventsSortIdMutation } from "../../../../graphql/operations/trialMode/localEvent/update-featured-events-sort-id";

import { FormItemMarket } from "../../components/FormItems";
import errorMessage from "../../utils/errorMessage";
import { DrawerActionType, MarketType } from "../../types";
import TableMenu from "../components/TableMenu";
import DraggableTable from "../components/DraggableTable";
import {
  FormItemUploadImage,
  FormItemNavigationUrl,
  FormItemPublishDate,
  FormItemStopDate,
} from "../components/FormItems";
import splitDataByIsInUse from "../utils/splitDataByIsInUse";
import { normalizeFeaturedEventsData } from "../utils/normalizeData";
import { FeaturedEventsDataType, FeaturedEventsFormDataType } from "../types";
import { createColumns } from "./columns";
import handleSubmit from "./handleSubmit";

export default function FeaturedEvents() {
  const initialMarket = getInitialMarket();

  const [displayType, setDisplayType] = useState<string>("In use");
  const [selectedMarket, setSelectedMarket] =
    useState<MarketType>(initialMarket);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [drawerAction, setDrawerAction] = useState<DrawerActionType>("Create");
  const [dataSource, setDataSource] = useState<FeaturedEventsDataType[]>([]);
  const [marketData, setMarketData] = useState<FeaturedEventsDataType[]>([]);
  const [inUseData, setInUseData] = useState<FeaturedEventsDataType[]>([]);
  const [notInUseData, setNotInUseData] = useState<FeaturedEventsDataType[]>(
    []
  );

  const [uploadFile] = useUploadFileMutation();
  const {
    data,
    loading: loadingOfGet,
    error: errorOfGet,
    refetch,
  } = useGetFeaturedEventsQuery({
    fetchPolicy: "no-cache",
  });
  const [createFeaturedEvent, { loading: loadingOfCreate }] =
    useCreateFeaturedEventMutation();
  const [updateFeaturedEvent, { loading: loadingOfUpdate }] =
    useUpdateFeaturedEventMutation();
  const [updateSortId, { loading: loadingOfUpdateSortId }] =
    useUpdateFeaturedEventsSortIdMutation();

  const [form] = Form.useForm();

  useEffect(() => {
    if (data) setDataSource(normalizeFeaturedEventsData(data));
  }, [data]);

  useEffect(() => {
    setMarketData(dataSource.filter(({ market }) => market === selectedMarket));
  }, [dataSource, selectedMarket]);

  useEffect(() => {
    const { data1, data2 } = splitDataByIsInUse(marketData);

    setInUseData(data1);
    setNotInUseData(data2);
  }, [marketData]);

  if (errorOfGet) errorMessage();
  if (loadingOfGet || loadingOfUpdateSortId) return <Spinner />;

  return (
    <div className="bg-white mx-5 my-6 px-6">
      <TableMenu
        form={form}
        setDisplayType={setDisplayType}
        selectedMarket={selectedMarket}
        setSelectedMarket={setSelectedMarket}
        setOpenDrawer={setOpenDrawer}
        setDrawerAction={setDrawerAction}
      />

      {displayType === "In use" ? (
        <DraggableTable
          eventType="FeaturedEvent"
          data={inUseData}
          setData={setInUseData}
          setDataSource={setDataSource}
          columns={createColumns(
            form,
            setOpenDrawer,
            setDrawerAction,
            refetch,
            true
          )}
          updateSortId={updateSortId}
          refetch={refetch}
        />
      ) : (
        <TableCustom
          className="custom-table-style"
          dataSource={[...notInUseData].sort(
            (a, b) => a.sortId - b.sortId || parseInt(b.key) - parseInt(a.key)
          )}
          columns={createColumns(form, setOpenDrawer, setDrawerAction, refetch)}
          pagination={false}
        />
      )}

      <DrawerCustom
        title={`${
          drawerAction === "Create" ? "Create new" : "Edit"
        } featured event`}
        name="create-new-featured-event"
        form={form}
        onFinish={(values: FeaturedEventsFormDataType) =>
          handleSubmit({
            values,
            form,
            drawerAction,
            uploadFile,
            createFeaturedEvent,
            updateFeaturedEvent,
            refetch,
            setOpenDrawer,
          })
        }
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        isLoading={loadingOfCreate || loadingOfUpdate}
      >
        <FormItemMarket
          modifiable={false}
          market={form.getFieldValue("market")}
        />
        <FormItemUploadImage resolution="1050px * 588px" />
        <FormItemNavigationUrl />
        <FormItemPublishDate />
        <FormItemStopDate />
      </DrawerCustom>
    </div>
  );
}
